import { defineStore } from "pinia";
import {computed, ref} from "vue";
import Api from "@/services/Api";
import Cache from "@/services/Cache";
import Emitter from "@/services/Emitter";

import type { TechnicianModel } from "@/models/TechnicianModel";

export const useTechStore = defineStore("tech", () => 
{
    const CACHE_KEY = "store_tech_data";
    const list = ref<Record<number, TechnicianModel>>({});
    const isInitialized = ref<boolean>(false);
    let initPromise: Promise<void> | null = null;
    
    const active = computed(() =>
    {
        return Object.values(list.value).filter(x => x.Active);
    });

    const init = async (): Promise<void> => {
        // If already initialized, return immediately
        if (isInitialized.value) return;
        
        // If initialization is in progress, return the existing promise
        if (initPromise) return initPromise;
        
        // Start new initialization
        initPromise = (async () => {
            try {
                // Check cache first
                const cached = await Cache.get(CACHE_KEY);
                if (cached && typeof cached === 'object' && Object.keys(cached).length > 0) {
                    list.value = cached;
                    isInitialized.value = true;
                    return;
                }
                
                // Load from server if cache is empty
                await reload();
            } finally {
                // Clear the promise when done (success or failure)
                initPromise = null;
            }
        })();
        
        return initPromise;
    };
    
    /**
     * reload the list of techs in the app.
     */
    const reload = async ():Promise<Record<number, TechnicianModel> | null> =>
    {
        let tmp = await Api.loadTechs() || [];
        let rec = {} as Record<number, TechnicianModel>;
        for (let i:number = 0; i < tmp.length; i++) {
            rec[tmp[i].Id] = tmp[i];            
        }
        list.value = rec;
        await Cache.set(CACHE_KEY, list.value);
        isInitialized.value = true;
        return rec;
    };

    const refresh = async (): Promise<void> => {
        await reload();
    };

    const getById = async (id:number):Promise<TechnicianModel | null> =>
    {
        await init();

        if (list.value[id]) {
            return list.value[id];
        }

        return null;
    };

    const getActive = async ():Promise<Array<TechnicianModel>> =>
    {
        await init();
        return active.value;
    };

    // Register refresh event listener
    Emitter.on("refresh:techStore", async () => {
        console.debug("refresh:techStore event received");
        await refresh();
    });

    // Auto-initialize on store creation
    init();

    return { list, active, reload, refresh, getById, getActive }
    
});